import { useState } from 'react';
import { login } from '../offline/sync.js';

import { API_BASE } from '../config.js';

interface Props {
  /** Dipanggil selepas log masuk berjaya, bersama butiran yang boleh diisi semula. */
  onToken: (token: string, meta: { email: string; slug: string }) => void;
  reason?: string;
  /** E-mel dan pautan kedai yang diisi semula supaya pemilik tidak perlu menaipnya setiap kali. */
  initialEmail?: string | undefined;
  initialSlug?: string | undefined;
  /**
   * Jalan keluar apabila borang ini dibuka DARIPADA POS.
   *
   * Kasir yang tersilap menekan "Admin" mesti boleh kembali bekerja tanpa memuat semula halaman atau
   * menutup pelayar.
   */
  onCancel?: () => void;
}

/**
 * Log masuk semula tanpa menyentuh pairing.
 *
 * Sesi pemilik luput selepas 12 jam. Sebelum ini, satu-satunya cara untuk kembali ke back office
 * ialah "sediakan semula peranti" — yang memerlukan kod pairing baharu (sah 15 minit) dan
 * memadam identiti peranti. Itu tidak munasabah untuk kerja harian.
 *
 * Log masuk hanya menukar token; kunci peranti, katalog dan outbox tidak disentuh.
 */
export function LoginForm({
  onToken,
  reason,
  onCancel,
  initialEmail,
  initialSlug,
}: Props): React.ReactElement {
  const [shopSlug, setShopSlug] = useState(initialSlug ?? '');
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);

  /**
   * Hantar pautan set semula.
   *
   * Mesej sengaja neutral: "jika e-mel itu berdaftar" — supaya borang ini tidak boleh digunakan
   * untuk menyemak sama ada sesuatu e-mel mempunyai akaun.
   */
  async function forgot(): Promise<void> {
    setError(null);
    setForgotMessage(null);
    if (!email.includes('@')) {
      setError('Masukkan e-mel anda dahulu, kemudian tekan "Lupa kata laluan?".');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error('Permintaan gagal. Cuba lagi.');
      setForgotMessage('Jika e-mel itu berdaftar, pautan set semula telah dihantar. Semak peti masuk (dan folder spam).');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Permintaan gagal.');
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await login(API_BASE, { email, password, shopSlug });
      onToken(session.token, { email, slug: shopSlug });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'log masuk gagal');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="setup">
      <h1>Log masuk</h1>
      <p className="muted">
        {reason ?? 'Sesi pemilik sudah luput.'} Peranti, katalog dan jualan yang belum dihantar tidak
        terjejas — hanya sesi baharu diperlukan.
      </p>

      <form onSubmit={submit}>
        <label>
          Pautan kedai
          <input
            value={shopSlug}
            onChange={(event) => setShopSlug(event.target.value)}
            placeholder="kedai-akuarium-72"
            required
          />
        </label>
        <label>
          E-mel
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label>
          Kata laluan
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>

        {error !== null && <p className="error">{error}</p>}

        {forgotMessage !== null && <p className="muted">{forgotMessage}</p>}

        <button type="button" className="login-forgot" onClick={() => void forgot()}>
          Lupa kata laluan?
        </button>

        {/*
          Dua tindakan pada satu baris: "Kembali ke POS" ialah jalan keluar, "Log masuk" ialah
          tindakan utama. Sebelum ini jalan keluar tergantung di bawah butang utama dan kelihatan
          seperti bahagian borang yang tersilap letak.
        */}
        <div className="login-actions">
          {onCancel !== undefined && (
            <button type="button" className="ghost" onClick={onCancel}>
              Kembali ke POS
            </button>
          )}
          <button type="submit" disabled={busy}>
            {busy ? 'Memproses…' : 'Log masuk'}
          </button>
        </div>
      </form>
    </div>
  );
}
